import { useContext } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { AuthContext } from "./components/AuthContext";
import { QueryKeys } from "./constants/QueryKeys";
import {
    changeUserPassword,
    getUser,
    login,
    logout,
    requestPasswordReset,
    submitTwoFactorChallenge,
    updateUserPassword,
    validateResetToken,
} from "./queries";

export const useRequestPasswordReset = () => useMutation(requestPasswordReset);

export const useLogout = () => {
    const queryClient = useQueryClient();

    return useMutation(logout, {
        onSuccess: () => {
            queryClient.setQueryData(QueryKeys.Authenticate, { user: null });
            window.open("/", "_self");
        },
    });
};

export const useLogin = () => useMutation(login);

export const useTwoFactorChallenge = () => useMutation(submitTwoFactorChallenge);

export const useGetUser = () => {
    const queryClient = useQueryClient();

    return useQuery(QueryKeys.Authenticate, getUser, {
        onError: () => {
            queryClient.setQueryData(QueryKeys.Authenticate, { user: null });
        },
        retry: false,
        refetchOnWindowFocus: true,
        refetchInterval: false,
    });
};

export const useAuth = () => {
    const context = useContext(AuthContext);

    if (context === undefined) {
        throw new Error("useAuth must be used within a AuthProvider");
    }

    return context;
};

export const useChangeUserPassword = () => useMutation(changeUserPassword);

export const useUpdateUserPassword = () => useMutation(updateUserPassword);

export const useValidateResetToken = (token: string, email?: string | null) =>
    useQuery<null | Error>(["token", token, email], () => validateResetToken(token, email), {
        retry: false,
    });
